import {
  buildStepToWorkflowMap,
  EventType,
  FailureContext,
  generateEventTypeMapDefaults,
  StepToWorkflowMap,
  WorkflowEventEventTypeMap,
  WorkflowEventMetaDataMapWithDefaults,
} from '@anduin/shared';
import { StatementPreviewErrorReason } from '@anduin/api';

/**
 * Workflow taxonomy owned by the collections module. Each value is the `workflow` string
 * emitted on tracking events for funnel grouping in downstream analytics (Pendo / LogRocket).
 */
export enum CollectionsWorkflowType {
  SETTINGS = 'COLLECTIONS_SETTINGS',
  STATEMENTS = 'COLLECTIONS_STATEMENTS',
  EXCLUSIONS = 'COLLECTIONS_EXCLUSIONS',
}

// VIEWED steps emit as SYSTEM here; FIRM-11152..55 sibling workflows mark VIEWED as USER
// per ticket text. Divergence is intentional — do not "fix" by aligning.

/** Configure the cadence / triggers / messaging that govern statement automation. */
export enum CollectionsSettingsStep {
  VIEWED = 'COLLECTIONS_SETTINGS_VIEWED',
  ABANDONED = 'COLLECTIONS_SETTINGS_ABANDONED',
  THROTTLING_CHANGED = 'COLLECTIONS_SETTINGS_THROTTLING_CHANGED',
  AUTO_APPROVE_TOGGLED = 'COLLECTIONS_SETTINGS_AUTO_APPROVE_TOGGLED',
  TRIGGER_ADDED = 'COLLECTIONS_SETTINGS_TRIGGER_ADDED',
  TRIGGER_REMOVED = 'COLLECTIONS_SETTINGS_TRIGGER_REMOVED',
  TRIGGER_CHANGED = 'COLLECTIONS_SETTINGS_TRIGGER_CHANGED',
  ADDITIONAL_RECIPIENT_ADDED = 'COLLECTIONS_SETTINGS_ADDITIONAL_RECIPIENT_ADDED',
  ADDITIONAL_RECIPIENT_REMOVED = 'COLLECTIONS_SETTINGS_ADDITIONAL_RECIPIENT_REMOVED',
  CLIENT_PARTNER_BUCKETS_CHANGED = 'COLLECTIONS_SETTINGS_CLIENT_PARTNER_BUCKETS_CHANGED',
  CLIENT_PARTNER_NOTIFICATION_TYPE_CHANGED = 'COLLECTIONS_SETTINGS_CLIENT_PARTNER_NOTIFICATION_TYPE_CHANGED',
  MESSAGE_EDITOR_OPENED = 'COLLECTIONS_SETTINGS_MESSAGE_EDITOR_OPENED',
  SAVE_SUBMITTED = 'COLLECTIONS_SETTINGS_SAVE_SUBMITTED',
  SAVE_SUCCEEDED = 'COLLECTIONS_SETTINGS_SAVE_SUCCEEDED',
  SAVE_FAILED = 'COLLECTIONS_SETTINGS_SAVE_FAILED',
  REVERT_CLICKED = 'COLLECTIONS_SETTINGS_REVERT_CLICKED',
}

/** Browse statement recipients, review history, approve/skip, undo. */
export enum CollectionsStatementsStep {
  VIEWED = 'COLLECTIONS_STATEMENTS_VIEWED',
  SEARCH_PERFORMED = 'COLLECTIONS_STATEMENTS_SEARCH_PERFORMED',
  SEARCH_RESULTED = 'COLLECTIONS_STATEMENTS_SEARCH_RESULTED',
  PARTNER_FILTERED = 'COLLECTIONS_STATEMENTS_PARTNER_FILTERED',
  SORT_CHANGED = 'COLLECTIONS_STATEMENTS_SORT_CHANGED',
  LOAD_MORE_CLICKED = 'COLLECTIONS_STATEMENTS_LOAD_MORE_CLICKED',
  RECIPIENT_SELECTED = 'COLLECTIONS_STATEMENTS_RECIPIENT_SELECTED',
  RECIPIENT_TOGGLED = 'COLLECTIONS_STATEMENTS_RECIPIENT_TOGGLED',
  STATEMENT_EDITED = 'COLLECTIONS_STATEMENTS_STATEMENT_EDITED',
  STATEMENT_LOAD_SUBMITTED = 'COLLECTIONS_STATEMENTS_STATEMENT_LOAD_SUBMITTED',
  STATEMENT_LOAD_SUCCEEDED = 'COLLECTIONS_STATEMENTS_STATEMENT_LOAD_SUCCEEDED',
  STATEMENT_LOAD_FAILED = 'COLLECTIONS_STATEMENTS_STATEMENT_LOAD_FAILED',
  ATTACHMENT_ADD_SUBMITTED = 'COLLECTIONS_STATEMENTS_ATTACHMENT_ADD_SUBMITTED',
  ATTACHMENT_ADD_SUCCEEDED = 'COLLECTIONS_STATEMENTS_ATTACHMENT_ADD_SUCCEEDED',
  ATTACHMENT_ADD_FAILED = 'COLLECTIONS_STATEMENTS_ATTACHMENT_ADD_FAILED',
  ATTACHMENT_REMOVE_SUBMITTED = 'COLLECTIONS_STATEMENTS_ATTACHMENT_REMOVE_SUBMITTED',
  ATTACHMENT_REMOVE_SUCCEEDED = 'COLLECTIONS_STATEMENTS_ATTACHMENT_REMOVE_SUCCEEDED',
  ATTACHMENT_REMOVE_FAILED = 'COLLECTIONS_STATEMENTS_ATTACHMENT_REMOVE_FAILED',
  APPROVE_SUBMITTED = 'COLLECTIONS_STATEMENTS_APPROVE_SUBMITTED',
  APPROVE_SUCCEEDED = 'COLLECTIONS_STATEMENTS_APPROVE_SUCCEEDED',
  APPROVE_FAILED = 'COLLECTIONS_STATEMENTS_APPROVE_FAILED',
  SKIP_SUBMITTED = 'COLLECTIONS_STATEMENTS_SKIP_SUBMITTED',
  SKIP_SUCCEEDED = 'COLLECTIONS_STATEMENTS_SKIP_SUCCEEDED',
  SKIP_FAILED = 'COLLECTIONS_STATEMENTS_SKIP_FAILED',
  TEST_EMAIL_SUBMITTED = 'COLLECTIONS_STATEMENTS_TEST_EMAIL_SUBMITTED',
  TEST_EMAIL_SUCCEEDED = 'COLLECTIONS_STATEMENTS_TEST_EMAIL_SUCCEEDED',
  TEST_EMAIL_FAILED = 'COLLECTIONS_STATEMENTS_TEST_EMAIL_FAILED',
  UNDO_SKIP_SUBMITTED = 'COLLECTIONS_STATEMENTS_UNDO_SKIP_SUBMITTED',
  UNDO_SKIP_SUCCEEDED = 'COLLECTIONS_STATEMENTS_UNDO_SKIP_SUCCEEDED',
  UNDO_SKIP_FAILED = 'COLLECTIONS_STATEMENTS_UNDO_SKIP_FAILED',
}

/** Manage which clients / contacts are excluded from automated statements. */
export enum CollectionsExclusionsStep {
  DIALOG_OPENED = 'COLLECTIONS_EXCLUSIONS_DIALOG_OPENED',
  DIALOG_CLOSED = 'COLLECTIONS_EXCLUSIONS_DIALOG_CLOSED',
  SEARCH_PERFORMED = 'COLLECTIONS_EXCLUSIONS_SEARCH_PERFORMED',
  SEARCH_RESULTED = 'COLLECTIONS_EXCLUSIONS_SEARCH_RESULTED',
  SORT_CHANGED = 'COLLECTIONS_EXCLUSIONS_SORT_CHANGED',
  LOAD_MORE_CLICKED = 'COLLECTIONS_EXCLUSIONS_LOAD_MORE_CLICKED',
  RECIPIENT_TOGGLED = 'COLLECTIONS_EXCLUSIONS_RECIPIENT_TOGGLED',
  HEADER_TOGGLED = 'COLLECTIONS_EXCLUSIONS_HEADER_TOGGLED',
  SAVE_SUBMITTED = 'COLLECTIONS_EXCLUSIONS_SAVE_SUBMITTED',
  SAVE_SUCCEEDED = 'COLLECTIONS_EXCLUSIONS_SAVE_SUCCEEDED',
  SAVE_FAILED = 'COLLECTIONS_EXCLUSIONS_SAVE_FAILED',
}

export type CollectionsWorkflowStep = CollectionsSettingsStep | CollectionsStatementsStep | CollectionsExclusionsStep;

export type CollectionsSettingsVersion = 'v1' | 'v2';
export type CollectionsModeLabel = 'CLIENT' | 'CONTACT';
export type StatementRecipientTypeLabel = 'CLIENT' | 'CONTACT_EMAIL';
export type SettingsChangedSection =
  | 'throttling'
  | 'triggers'
  | 'autoApprove'
  | 'message'
  | 'bucketMessage'
  | 'additionalRecipients'
  | 'clientPartner'
  | 'exclusions';

export type TriggerComparatorOperator = '<' | '<=' | '>' | '>=' | '=';

/** Context fields stamped onto every collections event by `CollectionsTrackService`. */
export interface CollectionsBaseMetadata {
  settingsVersion: CollectionsSettingsVersion;
  collectionsMode: CollectionsModeLabel;
}

/**
 * Statements-only extension of the base context. `activeFilters` (partner filter + search)
 * is meaningful only on the Statements dashboard — Settings/Exclusions surfaces do not stamp it.
 */
export interface CollectionsStatementsContext extends CollectionsBaseMetadata {
  /**
   * Comma-bracketed dimension list: leading + trailing comma, alphabetised. Examples:
   *   `` (none),
   *   `,partner,`,
   *   `,partner,search,`.
   */
  activeFilters: string;
}

export interface SettingsThrottlingToggledMetadata extends CollectionsBaseMetadata {
  enabled: boolean;
  days: number;
}

export interface SettingsAutoApproveToggledMetadata extends CollectionsBaseMetadata {
  enabled: boolean;
}

export interface SettingsTriggerChangeMetadata extends CollectionsBaseMetadata {
  triggerCount: number;
  /** Operator on the trigger being added/removed (e.g. `>=`, `<=`, `=`). Omitted when unavailable. */
  triggerOperator?: TriggerComparatorOperator;
  /** Threshold value on the trigger being added/removed (days). Omitted when unavailable. */
  triggerValue?: number;
}

export interface SettingsAdditionalRecipientChangeMetadata extends CollectionsBaseMetadata {
  recipientCount: number;
}

export interface SettingsClientPartnerBucketsChangedMetadata extends CollectionsBaseMetadata {
  bucketCount: number;
  /**
   * Comma-bracketed list of bucket identifiers the client partner is currently subscribed
   * to (e.g. `,ZERO_TO_THIRTY,SIXTY_TO_NINETY,`). Empty string when no buckets are selected.
   */
  buckets: string;
}

export interface SettingsClientPartnerNotificationTypeChangedMetadata extends CollectionsBaseMetadata {
  notificationType: 'CC' | 'BCC';
}

export interface SettingsMessageEditorOpenedMetadata extends CollectionsBaseMetadata {
  /** Which field on the message template the firm engaged with first. */
  field: 'subject' | 'headline' | 'message';
}

export interface SettingsAbandonedMetadata extends CollectionsBaseMetadata {
  /** Whether the user navigated away with unsaved (dirty) changes. */
  isDirty: boolean;
  /** Sections that had unsaved changes when the page was abandoned. */
  changedSections: SettingsChangedSection[];
}

export interface SettingsSaveMetadata extends CollectionsBaseMetadata {
  changedSections: SettingsChangedSection[];
}

export interface SettingsSaveSucceededMetadata extends SettingsSaveMetadata {
  operationDurationMs: number;
}

export interface SettingsSaveFailedMetadata extends SettingsSaveMetadata, FailureContext {
  operationDurationMs: number;
}

export interface StatementsSearchPerformedMetadata extends CollectionsStatementsContext {
  searchTermLength: number;
  sortField?: string;
  direction?: 'asc' | 'desc' | null;
}

/**
 * Emitted after `STATEMENTS_SEARCH_PERFORMED` once the backend returns results. Funnel pair:
 * `SEARCH_PERFORMED` measures intent, `SEARCH_RESULTED` measures success.
 */
export interface StatementsSearchResultedMetadata extends StatementsSearchPerformedMetadata {
  resultCount: number;
  hasResults: boolean;
}

export interface StatementsSortChangedMetadata extends CollectionsStatementsContext {
  sortField: string;
  direction: 'asc' | 'desc' | null;
  /** Position of this column in a multi-sort stack (0 = primary). 0 when there is only one sorted column. */
  sortOrder: number;
}

export interface StatementsLoadMoreClickedMetadata extends CollectionsStatementsContext {
  page: number;
}

export interface StatementsRecipientSelectedMetadata extends CollectionsStatementsContext {
  recipientType: StatementRecipientTypeLabel;
  hasTriggeredInvoices: boolean;
  /** Whether the recipient was clicked from search results vs from the paged list. */
  fromSearch: boolean;
}

export interface StatementsRecipientToggledMetadata extends CollectionsStatementsContext {
  enabled: boolean;
  recipientType: StatementRecipientTypeLabel;
}

export interface StatementsStatementActionMetadata extends CollectionsStatementsContext {
  /** Backend statement / action identifier. */
  statementId: number;
}

export interface StatementsStatementActionSucceededMetadata extends StatementsStatementActionMetadata {
  operationDurationMs: number;
}

export interface StatementsStatementActionFailedMetadata extends StatementsStatementActionMetadata, FailureContext {
  operationDurationMs: number;
}

export interface StatementsUndoSkipMetadata extends CollectionsStatementsContext {
  actionId: number;
}

export interface StatementsUndoSkipSucceededMetadata extends StatementsUndoSkipMetadata {
  operationDurationMs: number;
}

export interface StatementsUndoSkipFailedMetadata extends StatementsUndoSkipMetadata, FailureContext {
  operationDurationMs: number;
}

export interface StatementsStatementEditedMetadata extends CollectionsStatementsContext {
  statementId: number;
}

export interface StatementsStatementLoadMetadata extends CollectionsStatementsContext {
  statementId: number;
}

export interface StatementsStatementLoadSucceededMetadata extends StatementsStatementLoadMetadata {
  operationDurationMs: number;
}

export interface StatementsStatementLoadFailedMetadata extends StatementsStatementLoadMetadata, FailureContext {
  operationDurationMs: number;
  previewErrorReason?: StatementPreviewErrorReason;
}

export interface StatementsAttachmentChangeMetadata extends CollectionsStatementsContext {
  statementId: number;
}

export interface StatementsAttachmentChangeSucceededMetadata extends StatementsAttachmentChangeMetadata {
  operationDurationMs: number;
}

export interface StatementsAttachmentChangeFailedMetadata extends StatementsAttachmentChangeMetadata, FailureContext {
  operationDurationMs: number;
}

export interface ExclusionsSearchPerformedMetadata extends CollectionsBaseMetadata {
  searchTermLength: number;
}

export interface ExclusionsSearchResultedMetadata extends ExclusionsSearchPerformedMetadata {
  resultCount: number;
  hasResults: boolean;
}

export interface ExclusionsSortChangedMetadata extends CollectionsBaseMetadata {
  sortField: string;
  direction: 'asc' | 'desc' | null;
}

export interface ExclusionsLoadMoreClickedMetadata extends CollectionsBaseMetadata {
  page: number;
}

export interface ExclusionsRecipientToggledMetadata extends CollectionsBaseMetadata {
  excluded: boolean;
}

export interface ExclusionsHeaderToggledMetadata extends CollectionsBaseMetadata {
  selectedAll: boolean;
}

/**
 * Save-outcome metadata for exclusions. `addedCount` and `removedCount` replace the legacy
 * `updateCount`: a single aggregate prevented analytics from telling "firms are net-excluding
 * more clients" from "firms are un-excluding clients." `updateCount` remains for back-compat
 * and equals `addedCount + removedCount`.
 */
export interface ExclusionsSaveOutcomeMetadata extends CollectionsBaseMetadata {
  updateCount: number;
  addedCount: number;
  removedCount: number;
}

export interface ExclusionsSaveSucceededMetadata extends ExclusionsSaveOutcomeMetadata {
  operationDurationMs: number;
}

export interface ExclusionsSaveFailedMetadata extends ExclusionsSaveOutcomeMetadata, FailureContext {
  operationDurationMs: number;
}

interface CollectionsStepMetaDataOverrides {
  [CollectionsSettingsStep.VIEWED]: CollectionsBaseMetadata;
  [CollectionsSettingsStep.ABANDONED]: SettingsAbandonedMetadata;
  [CollectionsSettingsStep.THROTTLING_CHANGED]: SettingsThrottlingToggledMetadata;
  [CollectionsSettingsStep.AUTO_APPROVE_TOGGLED]: SettingsAutoApproveToggledMetadata;
  [CollectionsSettingsStep.TRIGGER_ADDED]: SettingsTriggerChangeMetadata;
  [CollectionsSettingsStep.TRIGGER_REMOVED]: SettingsTriggerChangeMetadata;
  [CollectionsSettingsStep.TRIGGER_CHANGED]: SettingsTriggerChangeMetadata;
  [CollectionsSettingsStep.ADDITIONAL_RECIPIENT_ADDED]: SettingsAdditionalRecipientChangeMetadata;
  [CollectionsSettingsStep.ADDITIONAL_RECIPIENT_REMOVED]: SettingsAdditionalRecipientChangeMetadata;
  [CollectionsSettingsStep.CLIENT_PARTNER_BUCKETS_CHANGED]: SettingsClientPartnerBucketsChangedMetadata;
  [CollectionsSettingsStep.CLIENT_PARTNER_NOTIFICATION_TYPE_CHANGED]: SettingsClientPartnerNotificationTypeChangedMetadata;
  [CollectionsSettingsStep.MESSAGE_EDITOR_OPENED]: SettingsMessageEditorOpenedMetadata;
  [CollectionsSettingsStep.SAVE_SUBMITTED]: SettingsSaveMetadata;
  [CollectionsSettingsStep.SAVE_SUCCEEDED]: SettingsSaveSucceededMetadata;
  [CollectionsSettingsStep.SAVE_FAILED]: SettingsSaveFailedMetadata;
  [CollectionsSettingsStep.REVERT_CLICKED]: CollectionsBaseMetadata;

  [CollectionsStatementsStep.VIEWED]: CollectionsStatementsContext;
  [CollectionsStatementsStep.SEARCH_PERFORMED]: StatementsSearchPerformedMetadata;
  [CollectionsStatementsStep.SEARCH_RESULTED]: StatementsSearchResultedMetadata;
  [CollectionsStatementsStep.PARTNER_FILTERED]: CollectionsStatementsContext;
  [CollectionsStatementsStep.SORT_CHANGED]: StatementsSortChangedMetadata;
  [CollectionsStatementsStep.LOAD_MORE_CLICKED]: StatementsLoadMoreClickedMetadata;
  [CollectionsStatementsStep.RECIPIENT_SELECTED]: StatementsRecipientSelectedMetadata;
  [CollectionsStatementsStep.RECIPIENT_TOGGLED]: StatementsRecipientToggledMetadata;
  [CollectionsStatementsStep.STATEMENT_EDITED]: StatementsStatementEditedMetadata;
  [CollectionsStatementsStep.STATEMENT_LOAD_SUBMITTED]: StatementsStatementLoadMetadata;
  [CollectionsStatementsStep.STATEMENT_LOAD_SUCCEEDED]: StatementsStatementLoadSucceededMetadata;
  [CollectionsStatementsStep.STATEMENT_LOAD_FAILED]: StatementsStatementLoadFailedMetadata;
  [CollectionsStatementsStep.ATTACHMENT_ADD_SUBMITTED]: StatementsAttachmentChangeMetadata;
  [CollectionsStatementsStep.ATTACHMENT_ADD_SUCCEEDED]: StatementsAttachmentChangeSucceededMetadata;
  [CollectionsStatementsStep.ATTACHMENT_ADD_FAILED]: StatementsAttachmentChangeFailedMetadata;
  [CollectionsStatementsStep.ATTACHMENT_REMOVE_SUBMITTED]: StatementsAttachmentChangeMetadata;
  [CollectionsStatementsStep.ATTACHMENT_REMOVE_SUCCEEDED]: StatementsAttachmentChangeSucceededMetadata;
  [CollectionsStatementsStep.ATTACHMENT_REMOVE_FAILED]: StatementsAttachmentChangeFailedMetadata;
  [CollectionsStatementsStep.APPROVE_SUBMITTED]: StatementsStatementActionMetadata;
  [CollectionsStatementsStep.APPROVE_SUCCEEDED]: StatementsStatementActionSucceededMetadata;
  [CollectionsStatementsStep.APPROVE_FAILED]: StatementsStatementActionFailedMetadata;
  [CollectionsStatementsStep.SKIP_SUBMITTED]: StatementsStatementActionMetadata;
  [CollectionsStatementsStep.SKIP_SUCCEEDED]: StatementsStatementActionSucceededMetadata;
  [CollectionsStatementsStep.SKIP_FAILED]: StatementsStatementActionFailedMetadata;
  [CollectionsStatementsStep.TEST_EMAIL_SUBMITTED]: StatementsStatementActionMetadata;
  [CollectionsStatementsStep.TEST_EMAIL_SUCCEEDED]: StatementsStatementActionSucceededMetadata;
  [CollectionsStatementsStep.TEST_EMAIL_FAILED]: StatementsStatementActionFailedMetadata;
  [CollectionsStatementsStep.UNDO_SKIP_SUBMITTED]: StatementsUndoSkipMetadata;
  [CollectionsStatementsStep.UNDO_SKIP_SUCCEEDED]: StatementsUndoSkipSucceededMetadata;
  [CollectionsStatementsStep.UNDO_SKIP_FAILED]: StatementsUndoSkipFailedMetadata;

  [CollectionsExclusionsStep.DIALOG_OPENED]: CollectionsBaseMetadata;
  [CollectionsExclusionsStep.DIALOG_CLOSED]: CollectionsBaseMetadata;
  [CollectionsExclusionsStep.SEARCH_PERFORMED]: ExclusionsSearchPerformedMetadata;
  [CollectionsExclusionsStep.SEARCH_RESULTED]: ExclusionsSearchResultedMetadata;
  [CollectionsExclusionsStep.SORT_CHANGED]: ExclusionsSortChangedMetadata;
  [CollectionsExclusionsStep.LOAD_MORE_CLICKED]: ExclusionsLoadMoreClickedMetadata;
  [CollectionsExclusionsStep.RECIPIENT_TOGGLED]: ExclusionsRecipientToggledMetadata;
  [CollectionsExclusionsStep.HEADER_TOGGLED]: ExclusionsHeaderToggledMetadata;
  [CollectionsExclusionsStep.SAVE_SUBMITTED]: ExclusionsSaveOutcomeMetadata;
  [CollectionsExclusionsStep.SAVE_SUCCEEDED]: ExclusionsSaveSucceededMetadata;
  [CollectionsExclusionsStep.SAVE_FAILED]: ExclusionsSaveFailedMetadata;
}

export type CollectionsWorkflowStepMetaDataMap = WorkflowEventMetaDataMapWithDefaults<
  CollectionsWorkflowStep,
  CollectionsStepMetaDataOverrides
>;

/**
 * Step-specific metadata with the base + statements-context fields removed.
 * `CollectionsTrackService.trackWorkflow` accepts this shape so call sites pass only the
 * per-step extras; the wrapper resolves `settingsVersion` / `collectionsMode` at emit time,
 * and `activeFilters` for Statements steps only.
 */
export type CollectionsStepMetadataInput<E extends CollectionsWorkflowStep> = Omit<
  CollectionsWorkflowStepMetaDataMap[E],
  keyof CollectionsStatementsContext
>;

const COLLECTIONS_WORKFLOW_STEPS = {
  [CollectionsWorkflowType.SETTINGS]: CollectionsSettingsStep,
  [CollectionsWorkflowType.STATEMENTS]: CollectionsStatementsStep,
  [CollectionsWorkflowType.EXCLUSIONS]: CollectionsExclusionsStep,
} as const;

export const COLLECTIONS_STEP_TO_WORKFLOW: StepToWorkflowMap<typeof COLLECTIONS_WORKFLOW_STEPS> =
  buildStepToWorkflowMap(COLLECTIONS_WORKFLOW_STEPS);

export const COLLECTIONS_STEP_TO_EVENT_TYPE: WorkflowEventEventTypeMap<CollectionsWorkflowStep> = {
  ...generateEventTypeMapDefaults(CollectionsSettingsStep, EventType.USER),
  ...generateEventTypeMapDefaults(CollectionsStatementsStep, EventType.USER),
  ...generateEventTypeMapDefaults(CollectionsExclusionsStep, EventType.USER),
  [CollectionsSettingsStep.VIEWED]: EventType.SYSTEM,
  [CollectionsSettingsStep.ABANDONED]: EventType.SYSTEM,
  [CollectionsSettingsStep.SAVE_SUCCEEDED]: EventType.SYSTEM,
  [CollectionsSettingsStep.SAVE_FAILED]: EventType.ERROR,
  [CollectionsStatementsStep.VIEWED]: EventType.SYSTEM,
  [CollectionsStatementsStep.SEARCH_RESULTED]: EventType.SYSTEM,
  [CollectionsStatementsStep.STATEMENT_LOAD_SUBMITTED]: EventType.SYSTEM,
  [CollectionsStatementsStep.STATEMENT_LOAD_SUCCEEDED]: EventType.SYSTEM,
  [CollectionsStatementsStep.STATEMENT_LOAD_FAILED]: EventType.ERROR,
  [CollectionsStatementsStep.ATTACHMENT_ADD_SUCCEEDED]: EventType.SYSTEM,
  [CollectionsStatementsStep.ATTACHMENT_ADD_FAILED]: EventType.ERROR,
  [CollectionsStatementsStep.ATTACHMENT_REMOVE_SUCCEEDED]: EventType.SYSTEM,
  [CollectionsStatementsStep.ATTACHMENT_REMOVE_FAILED]: EventType.ERROR,
  [CollectionsStatementsStep.APPROVE_SUCCEEDED]: EventType.SYSTEM,
  [CollectionsStatementsStep.APPROVE_FAILED]: EventType.ERROR,
  [CollectionsStatementsStep.SKIP_SUCCEEDED]: EventType.SYSTEM,
  [CollectionsStatementsStep.SKIP_FAILED]: EventType.ERROR,
  [CollectionsStatementsStep.TEST_EMAIL_SUCCEEDED]: EventType.SYSTEM,
  [CollectionsStatementsStep.TEST_EMAIL_FAILED]: EventType.ERROR,
  [CollectionsStatementsStep.UNDO_SKIP_SUCCEEDED]: EventType.SYSTEM,
  [CollectionsStatementsStep.UNDO_SKIP_FAILED]: EventType.ERROR,
  [CollectionsExclusionsStep.DIALOG_OPENED]: EventType.SYSTEM,
  [CollectionsExclusionsStep.DIALOG_CLOSED]: EventType.SYSTEM,
  [CollectionsExclusionsStep.SEARCH_RESULTED]: EventType.SYSTEM,
  [CollectionsExclusionsStep.SAVE_SUCCEEDED]: EventType.SYSTEM,
  [CollectionsExclusionsStep.SAVE_FAILED]: EventType.ERROR,
};
